import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchReceipt, getPartnerReceipts, reviewUserReceipt } from '../../services/TransactionService';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useGuidedTour from '../../hooks/useGuidedTour';
import { showMutationError } from '../../utils/showMutationError';
import {
    MANAGE_RECEIPTS_OVERVIEW_TOUR_ID,
    buildManageReceiptsOverviewTourSteps,
} from './ReceiptReviewsMainTable.tour';

const statusBadgeClass = (status) => {
    if (status === 'approved') return 'badge-success'
    if (status === 'pending')  return 'badge-warning'
    return 'badge-danger'
}

const DeletedBadge = () => (
    <span className="badge badge-rounded badge-danger ms-1" style={{ fontSize: '0.65rem' }}>deleted</span>
)

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    )
}

const ReceiptReviewsMainTable = () => {
    const authUser = useSelector(state => state.auth.authUser);
    const partnerId = authUser?.partner_id;
    const queryClient = useQueryClient();

    useGuidedTour(MANAGE_RECEIPTS_OVERVIEW_TOUR_ID, buildManageReceiptsOverviewTourSteps());

    const [currentPage,     setCurrentPage]     = useState(1);
    const cardRef                               = useRef(null);

    const goToPage = (nextPage) => {
        setCurrentPage(nextPage);
        if (cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
    const [dateFrom,        setDateFrom]        = useState('');
    const [dateTo,          setDateTo]          = useState('');
    const [statusFilter,    setStatusFilter]    = useState('');
    const [search,          setSearch]          = useState('');
    const [viewingReceipt,  setViewingReceipt]  = useState(null);
    const [decision,        setDecision]        = useState('');
    const [reason,          setReason]          = useState('');
    const [confirmModal,    setConfirmModal]    = useState(false);
    const [reviewFieldErrors, setReviewFieldErrors] = useState({});
    const [receiptImageUrl, setReceiptImageUrl] = useState(null);
    const [receiptImageMime, setReceiptImageMime] = useState('');

    const debouncedSearch = useDebouncedValue(search, 300);

    const receiptsQuery = useQuery({
        queryKey: ['partnerReceipts', partnerId, currentPage, debouncedSearch, dateFrom, dateTo, statusFilter],
        queryFn:  () => getPartnerReceipts(partnerId, {
            page:   currentPage,
            search: debouncedSearch,
            dateFrom,
            dateTo,
            status: statusFilter,
        }),
        enabled:         !!partnerId,
        placeholderData: keepPreviousData,
        staleTime:       0,
    });

    const receipts   = receiptsQuery.data?.data?.data ?? [];
    const totalPages = receiptsQuery.data?.data?.meta?.last_page ?? 1;

    const { data: receiptBlob, isLoading: receiptImageLoading } = useQuery({
        queryKey:  ['adminReceiptImage', viewingReceipt?.id],
        queryFn:   () => fetchReceipt(partnerId, viewingReceipt.created_by_id, viewingReceipt.id),
        enabled:   !!viewingReceipt,
        staleTime: Infinity,
        retry:     1,
    });

    useEffect(() => {
        if (receiptBlob) {
            const url = URL.createObjectURL(receiptBlob);
            setReceiptImageUrl(url);
            setReceiptImageMime(receiptBlob.type || '');
            return () => {
                URL.revokeObjectURL(url);
                setReceiptImageUrl(null);
                setReceiptImageMime('');
            };
        }
    }, [receiptBlob]);

    const reviewMutation = useMutation({
        mutationFn: (payload) => reviewUserReceipt(partnerId, payload),
        onSuccess: () => {
            toast.success('Review successful.');
            queryClient.invalidateQueries({ queryKey: ['partnerReceipts', partnerId] });
            setConfirmModal(false);
            setViewingReceipt(null);
            setDecision('');
            setReason('');
            setReviewFieldErrors({});
        },
        onError: (err) => showMutationError(err, {
            fallback: 'Review failed. Please try again later.',
            onFieldErrors: setReviewFieldErrors,
        }),
    });

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setCurrentPage(1);
    };

    const applyFilterValue = (setter) => (value) => {
        setter(value);
        setCurrentPage(1);
    };

    const hasActiveFilter = dateFrom !== '' || dateTo !== '' || statusFilter !== '' || search !== '';

    const handleClear = () => {
        setDateFrom('');
        setDateTo('');
        setStatusFilter('');
        setSearch('');
        setCurrentPage(1);
    };

    const handleReceiptReview = () => {
        if (!decision) return toast.error('Please select approve or reject.');
        if (decision === 'reject' && !reason.trim()) return toast.error('Please enter a reason for rejection.');

        reviewMutation.mutate({
            receipt_id: viewingReceipt.id,
            status:     decision === 'approved' ? 'approved' : 'rejected',
            ...(decision === 'reject' && { reject_reason: reason.trim() }),
        });
    };

    return (
        <>
        <div className="row">
          <div className="col-12">
            <div className="card" ref={cardRef} data-tour="manage-receipts-card">
              <div className="card-header">
                <h4 className="card-title mb-0">Manage Receipts</h4>
              </div>
              <div className="card-body p-0">
                <div className="px-3 pt-3">
                {(() => {
                    const statusOptions = [
                        { value: '',         label: 'All statuses' },
                        { value: 'pending',  label: 'Pending' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' },
                    ]
                    const currentStatusLabel = statusOptions.find(o => o.value === statusFilter)?.label ?? 'All statuses'
                    const fromLabel = dateFrom ? `From ${dateFrom}` : 'From date'
                    const toLabel   = dateTo   ? `To ${dateTo}`     : 'To date'
                    return (
                        <div className="mb-3" data-tour="manage-receipts-filters">
                            <input
                                type="text"
                                className="form-control mb-2"
                                placeholder="Search"
                                value={search}
                                onChange={handleFilterChange(setSearch)}
                            />
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                <Dropdown autoClose="outside">
                                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                                        <span className="flex-grow-1 text-start">{fromLabel}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="p-2">
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={dateFrom}
                                            onChange={handleFilterChange(setDateFrom)}
                                        />
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Dropdown autoClose="outside">
                                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                                        <span className="flex-grow-1 text-start">{toLabel}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="p-2">
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={dateTo}
                                            onChange={handleFilterChange(setDateTo)}
                                        />
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                                        <span className="flex-grow-1 text-start">{currentStatusLabel}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {statusOptions.map(option => (
                                            <Dropdown.Item
                                                key={option.value || 'all'}
                                                active={statusFilter === option.value}
                                                onClick={() => applyFilterValue(setStatusFilter)(option.value)}
                                            >
                                                {option.label}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                                {hasActiveFilter && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-decoration-none p-0 ms-1"
                                        onClick={handleClear}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    )
                })()}
                </div>
                {receiptsQuery.isLoading ? (
                    <div className="d-flex justify-content-center py-4">
                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                ) : receiptsQuery.error ? (
                    <p className="text-center text-danger py-4 mb-0">
                        Failed to load receipts. Please try again.
                    </p>
                ) : receipts.length === 0 ? (
                    <p className="text-center text-muted py-4 mb-0">No entries found.</p>
                ) : (() => {
                    const reviewButton = (item) => (
                        <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => setViewingReceipt(item)}
                            aria-label="Review receipt"
                        >
                            <i className="fa fa-eye" aria-hidden="true" />
                        </button>
                    );
                    return (
                        <div data-tour="manage-receipts-list">
                            <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                <table className="table verticle-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">Expense Tags</th>
                                            <th scope="col">Amount</th>
                                            <th scope="col">Date</th>
                                            <th scope="col">Created By</th>
                                            <th scope="col">Status</th>
                                            <th scope="col">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receipts.map((item) => (
                                            <tr key={item.id}>
                                                <td style={{ overflowWrap: 'anywhere' }}>{item.tags?.join(', ') || '-'}</td>
                                                <td className="text-nowrap">{item.amount}</td>
                                                <td className="text-nowrap">{item.date}</td>
                                                <td style={{ overflowWrap: 'anywhere' }}>
                                                    {item.created_by}
                                                    {item.created_by_deleted && <DeletedBadge />}
                                                </td>
                                                <td>
                                                    <span className={`badge badge-rounded ${statusBadgeClass(item.status?.toLowerCase())}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td>{reviewButton(item)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                <table className="table verticle-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">Receipt</th>
                                            <th scope="col">User &amp; Status</th>
                                            <th scope="col" className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receipts.map((item) => (
                                            <tr key={item.id} className="align-top">
                                                <td>
                                                    <div style={{ overflowWrap: 'anywhere' }}>{item.tags?.join(', ') || '-'}</div>
                                                    <div className="fw-semibold mt-1">{item.amount}</div>
                                                    <div className="text-muted small">{item.date}</div>
                                                </td>
                                                <td>
                                                    <div style={{ overflowWrap: 'anywhere' }}>
                                                        {item.created_by}
                                                        {item.created_by_deleted && <DeletedBadge />}
                                                    </div>
                                                    <div className="mt-1">
                                                        <span className={`badge badge-rounded ${statusBadgeClass(item.status?.toLowerCase())}`}>
                                                            {item.status}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="text-end">{reviewButton(item)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <ul className="list-group list-group-flush d-block d-md-none">
                                {receipts.map((item) => (
                                    <li key={item.id} className="list-group-item py-3">
                                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                            <div className="d-flex align-items-center flex-wrap gap-3" style={{ minWidth: 0 }}>
                                                <span style={{ overflowWrap: 'anywhere' }}>{item.tags?.join(', ') || '-'}</span>
                                                <span className={`badge badge-rounded ${statusBadgeClass(item.status?.toLowerCase())}`}>
                                                    {item.status}
                                                </span>
                                            </div>
                                            <div className="flex-shrink-0">{reviewButton(item)}</div>
                                        </div>
                                        <div className="fw-semibold mb-1">{item.amount}</div>
                                        <div className="text-muted small">Date: {item.date}</div>
                                        <div className="text-muted small" style={{ overflowWrap: 'anywhere' }}>
                                            By: {item.created_by}
                                            {item.created_by_deleted && <DeletedBadge />}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })()}
                <PaginationBar
                    page={currentPage}
                    lastPage={totalPages}
                    isLoading={receiptsQuery.isFetching}
                    onPrev={() => goToPage(Math.max(currentPage - 1, 1))}
                    onNext={() => goToPage(Math.min(currentPage + 1, totalPages))}
                />
              </div>
            </div>
          </div>
        </div>

            {viewingReceipt && !confirmModal && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Review Receipt</h5>
                                    <button type="button" className="btn-close" onClick={() => setViewingReceipt(null)} />
                                </div>
                                <div className="modal-body">
                                    {receiptImageLoading ? (
                                        <div className="text-center mb-3">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading image…</span>
                                            </div>
                                        </div>
                                    ) : receiptImageUrl ? (
                                        receiptImageMime === 'application/pdf' ? (
                                            <div className="mb-3">
                                                <iframe
                                                    src={receiptImageUrl}
                                                    title="Receipt PDF"
                                                    className="w-100 mb-2"
                                                    style={{ height: '400px', border: '1px solid #dee2e6' }}
                                                />
                                                <a href={receiptImageUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                                    Open PDF in a new tab
                                                </a>
                                            </div>
                                        ) : (
                                            <img src={receiptImageUrl} alt="Receipt" className="img-fluid mb-3" style={{ maxHeight: '300px' }} />
                                        )
                                    ) : (
                                        <p className="text-muted mb-3">Image unavailable.</p>
                                    )}
                                    <ul className="list-group mb-3">
                                        <li className="list-group-item"><strong>Tags:</strong> {viewingReceipt.tags?.join(', ')}</li>
                                        <li className="list-group-item"><strong>Amount:</strong> {viewingReceipt.amount}</li>
                                        <li className="list-group-item"><strong>Date:</strong> {viewingReceipt.date}</li>
                                        <li className="list-group-item"><strong>MPESA Code:</strong> {viewingReceipt.mpesa_transaction_code}</li>
                                        <li className="list-group-item"><strong>Uploaded by:</strong> {viewingReceipt.created_by}</li>
                                        <li className="list-group-item"><strong>Status:</strong> {viewingReceipt.status}</li>
                                        {viewingReceipt.status_slug !== 'pending' && viewingReceipt.reviewer && (
                                            <li className="list-group-item"><strong>Reviewed by:</strong> {viewingReceipt.reviewer}</li>
                                        )}
                                        {viewingReceipt.status_slug === 'rejected' && viewingReceipt.reject_reason && (
                                            <li className="list-group-item text-danger">
                                                <strong>Reason for rejection:</strong> {viewingReceipt.reject_reason}
                                            </li>
                                        )}
                                    </ul>
                                    {viewingReceipt.status_slug === 'pending' && viewingReceipt.created_by_id === authUser?.id && (
                                        <div className="alert alert-info mb-0" role="note">
                                            This is a receipt you uploaded. Someone else on your account has to review it. Ask another super admin or an accountant to sign it off, or invite one from Users.
                                        </div>
                                    )}
                                    {viewingReceipt.status_slug === 'pending' && viewingReceipt.created_by_id !== authUser?.id && (
                                        <>
                                            <div className="mb-3">
                                                <label htmlFor="review-decision" className="form-label">Decision</label>
                                                <select
                                                    id="review-decision"
                                                    className={`form-control${reviewFieldErrors.status ? ' is-invalid' : ''}`}
                                                    value={decision}
                                                    onChange={(e) => {
                                                        setDecision(e.target.value);
                                                        if (reviewFieldErrors.status) {
                                                            setReviewFieldErrors((prev) => {
                                                                const next = { ...prev };
                                                                delete next.status;
                                                                return next;
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select...</option>
                                                    <option value="approved">Approve</option>
                                                    <option value="reject">Reject</option>
                                                </select>
                                                {reviewFieldErrors.status && (
                                                    <div className="invalid-feedback d-block">{reviewFieldErrors.status}</div>
                                                )}
                                            </div>
                                            {decision === 'reject' && (
                                                <div className="mb-3">
                                                    <label htmlFor="review-reason" className="form-label">Reason for Rejection</label>
                                                    <textarea
                                                        id="review-reason"
                                                        className={`form-control${reviewFieldErrors.reject_reason ? ' is-invalid' : ''}`}
                                                        value={reason}
                                                        onChange={(e) => {
                                                            setReason(e.target.value);
                                                            if (reviewFieldErrors.reject_reason) {
                                                                setReviewFieldErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next.reject_reason;
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    {reviewFieldErrors.reject_reason && (
                                                        <div className="invalid-feedback d-block">{reviewFieldErrors.reject_reason}</div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => setViewingReceipt(null)}>
                                        {viewingReceipt.status_slug === 'pending' && viewingReceipt.created_by_id !== authUser?.id ? 'Cancel' : 'Close'}
                                    </button>
                                    {viewingReceipt.status_slug === 'pending' && viewingReceipt.created_by_id !== authUser?.id && (
                                        <button
                                            className="btn btn-primary"
                                            disabled={!decision || (decision === 'reject' && !reason.trim())}
                                            onClick={() => setConfirmModal(true)}
                                        >
                                            Submit
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" />
                </>
            )}

            {confirmModal && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirm Action</h5>
                                    <button className="btn-close" onClick={() => setConfirmModal(false)} />
                                </div>
                                <div className="modal-body">
                                    {decision === 'approved' && viewingReceipt && (
                                        <p className="mb-0">
                                            Approve the receipt for <strong>{viewingReceipt.amount}</strong> dated <strong>{viewingReceipt.date}</strong> uploaded by <strong>{viewingReceipt.created_by}</strong>? An approval email will be sent to the uploader.
                                        </p>
                                    )}
                                    {decision === 'reject' && viewingReceipt && (
                                        <p className="mb-0">
                                            Reject the receipt for <strong>{viewingReceipt.amount}</strong> dated <strong>{viewingReceipt.date}</strong> uploaded by <strong>{viewingReceipt.created_by}</strong>? The uploader will get an email with the rejection reason.
                                        </p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => setConfirmModal(false)}>Cancel</button>
                                    <button className="btn btn-primary" onClick={handleReceiptReview} disabled={reviewMutation.isPending}>
                                        {reviewMutation.isPending ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                Processing
                                            </>
                                        ) : 'Yes, Submit'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" />
                </>
            )}
        </>
    );
};

export default ReceiptReviewsMainTable;
